import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import api from '../services/api';
import TextField from '../components/common/TextField';

type RequestStatus = 'Pendiente' | 'Convertido' | 'Cancelado';
type Priority = 'Alta' | 'Media' | 'Baja';

export type PurchaseRequestItem = {
  id?: number;
  productId?: number;
  materialId?: number;
  productName?: string;
  quantity: number;
  notes?: string;
};

export type PurchaseRequest = {
  id: number;
  requestNumber: string;
  requestedBy?: string;
  priority: Priority;
  requiredDate?: string | null;
  status: RequestStatus;
  notes?: string;
  purchaseId?: number;
  purchaseReference?: string;
  createdAt: string;
  items: PurchaseRequestItem[];
};

// Cada fila del form elige de dónde sale el ítem: producto del catálogo, materia
// prima, o texto libre (insumos / gastos de fábrica que no están cargados).
type ItemSource = 'product' | 'material' | 'free';
type FormItem = PurchaseRequestItem & { source: ItemSource };

type ProductOption = { id: number; name: string; sku?: string };
type MaterialOption = { id: number; name: string; code?: string };

const PRIORITY_COLOR: Record<Priority, 'error' | 'warning' | 'default'> = {
  Alta: 'error',
  Media: 'warning',
  Baja: 'default',
};

const STATUS_COLOR: Record<RequestStatus, 'info' | 'success' | 'default'> = {
  Pendiente: 'info',
  Convertido: 'success',
  Cancelado: 'default',
};

const emptyForm = () => ({
  priority: 'Media' as Priority,
  requiredDate: '',
  notes: '',
  items: [] as FormItem[],
});

// Fechas date-only guardadas como medianoche UTC: formatear en UTC para que no se
// corran un día hacia atrás en hora argentina.
const fmtDateUtc = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '-');
const isPastDate = (iso?: string | null) => !!iso && iso.substring(0, 10) < new Date().toLocaleDateString('en-CA');

export const PurchaseRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<'Todos' | RequestStatus>('Pendiente');
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseRequest | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [materials, setMaterials] = useState<MaterialOption[]>([]);

  const load = async () => {
    try {
      const res = await api.getPurchaseRequests(statusFilter === 'Todos' ? undefined : statusFilter);
      setRequests(res.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar los pedidos internos');
      setRequests([]);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getProducts({ page: 1, pageSize: 500 });
        setProducts((res.products || []).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku })));
      } catch { setProducts([]); }
      try {
        const res = await api.getMaterials({ page: 1, pageSize: 500 });
        setMaterials((res.materials || []).map((m: any) => ({ id: m.id, name: m.name, code: m.code })));
      } catch { setMaterials([]); }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (r: PurchaseRequest) => {
    setEditing(r);
    setForm({
      priority: r.priority,
      requiredDate: r.requiredDate ? r.requiredDate.substring(0, 10) : '',
      notes: r.notes || '',
      items: (r.items || []).map(it => ({
        ...it,
        source: it.productId ? 'product' : it.materialId ? 'material' : 'free',
      })),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const items = form.items
      .filter(it => (it.quantity || 0) > 0 && (it.productId || it.materialId || (it.productName || '').trim()))
      .map(it => ({
        productId: it.source === 'product' ? it.productId : undefined,
        materialId: it.source === 'material' ? it.materialId : undefined,
        productName: it.productName || undefined,
        quantity: Number(it.quantity) || 0,
        notes: it.notes?.trim() || undefined,
      }));
    if (items.length === 0) { setError('Agregá al menos un ítem con cantidad'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        priority: form.priority,
        requiredDate: form.requiredDate || undefined,
        notes: form.notes || undefined,
        items,
      };
      if (editing) {
        await api.updatePurchaseRequest(editing.id, payload);
      } else {
        await api.createPurchaseRequest(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar el pedido');
    } finally {
      setSaving(false);
    }
  };

  const isOverdue = (r: PurchaseRequest) =>
    r.status === 'Pendiente' && isPastDate(r.requiredDate);

  const setItem = (idx: number, patch: Partial<FormItem>) =>
    setForm(f => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Pedidos internos</Typography>
          <Typography variant="body2" color="text.secondary">
            Cargá lo que hace falta comprar. Después, quien compra lo convierte en una orden de compra (opcional).
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>Nuevo pedido interno</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Pedidos</Typography>
            <TextField
              select
              size="small"
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              sx={{ minWidth: 180 }}
            >
              {['Todos', 'Pendiente', 'Convertido', 'Cancelado'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Solicitado por</TableCell>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Fecha requerida</TableCell>
                  <TableCell>Ítems</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Orden de compra</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} sx={isOverdue(r) ? { bgcolor: 'warning.50' } : undefined}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.requestNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(r.createdAt).toLocaleDateString('es-AR')}</Typography>
                    </TableCell>
                    <TableCell>{r.requestedBy || '-'}</TableCell>
                    <TableCell><Chip size="small" label={r.priority} color={PRIORITY_COLOR[r.priority] || 'default'} /></TableCell>
                    <TableCell>
                      {r.requiredDate
                        ? <Typography variant="body2" color={isOverdue(r) ? 'error' : undefined} fontWeight={isOverdue(r) ? 700 : undefined}>
                            {fmtDateUtc(r.requiredDate)}
                          </Typography>
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={(r.items || []).map(it => `${it.productName || '-'} x ${it.quantity}`).join(', ')}>
                        {(r.items || []).map(it => `${it.productName || '-'} x ${it.quantity}`).join(', ') || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} /></TableCell>
                    <TableCell>{r.purchaseReference || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" noWrap title={r.notes || ''}>{r.notes || '-'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {r.status === 'Pendiente' && (
                          <Tooltip title="Crear la orden de compra a partir de este pedido">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ShoppingCartCheckoutIcon />}
                              onClick={() => navigate('/purchases', { state: { purchaseRequest: r } })}
                            >
                              Generar OC
                            </Button>
                          </Tooltip>
                        )}
                        {r.status === 'Pendiente' && (
                          <Button size="small" variant="outlined" onClick={() => openEdit(r)}>Editar</Button>
                        )}
                        {r.status === 'Pendiente' && (
                          <Button size="small" color="warning" onClick={async () => {
                            if (!window.confirm(`¿Cancelar el pedido ${r.requestNumber}?`)) return;
                            try { await api.cancelPurchaseRequest(r.id); await load(); }
                            catch (e: any) { setError(e?.response?.data?.message || 'No se pudo cancelar el pedido'); }
                          }}>Cancelar</Button>
                        )}
                        {r.status === 'Pendiente' && (
                          <IconButton size="small" color="error" onClick={async () => {
                            if (!window.confirm(`¿Eliminar el pedido ${r.requestNumber}?`)) return;
                            try { await api.deletePurchaseRequest(r.id); await load(); }
                            catch (e: any) { setError(e?.response?.data?.message || 'No se pudo eliminar el pedido'); }
                          }}><DeleteIcon fontSize="small" /></IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No hay pedidos internos{statusFilter !== 'Todos' ? ` en estado ${statusFilter}` : ''}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? `Editar pedido ${editing.requestNumber}` : 'Nuevo pedido interno'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth label="Prioridad" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
              >
                {(['Alta', 'Media', 'Baja'] as Priority[]).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth type="date" label="Fecha requerida" InputLabelProps={{ shrink: true }}
                value={form.requiredDate}
                onChange={(e) => setForm({ ...form, requiredDate: e.target.value })}
                helperText="Para qué día se necesita"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={2} label="Notas"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Ítems</Typography>
              <Button variant="outlined" size="small" onClick={() =>
                setForm({ ...form, items: [...form.items, { source: 'product', quantity: 1, productName: '' }] })
              }>Agregar fila</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 150 }}>Origen</TableCell>
                  <TableCell>Ítem</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Cantidad</TableCell>
                  <TableCell sx={{ width: 200 }}>Notas</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        select size="small" fullWidth value={it.source}
                        onChange={(e) => setItem(idx, { source: e.target.value as ItemSource, productId: undefined, materialId: undefined, productName: '' })}
                      >
                        <MenuItem value="product">Producto</MenuItem>
                        <MenuItem value="material">Materia prima</MenuItem>
                        <MenuItem value="free">Texto libre</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      {it.source === 'product' && (
                        <Autocomplete
                          size="small"
                          options={products}
                          getOptionLabel={(op) => `${op.sku ? `${op.sku} — ` : ''}${op.name}`}
                          value={products.find(p => p.id === it.productId) || null}
                          onChange={(_, val) => setItem(idx, { productId: val?.id, productName: val?.name || '' })}
                          renderInput={(params) => <TextField {...params} placeholder="Producto" />}
                        />
                      )}
                      {it.source === 'material' && (
                        <Autocomplete
                          size="small"
                          options={materials}
                          getOptionLabel={(op) => `${op.code ? `${op.code} — ` : ''}${op.name}`}
                          value={materials.find(m => m.id === it.materialId) || null}
                          onChange={(_, val) => setItem(idx, { materialId: val?.id, productName: val?.name || '' })}
                          renderInput={(params) => <TextField {...params} placeholder="Materia prima" />}
                        />
                      )}
                      {it.source === 'free' && (
                        <TextField
                          size="small" fullWidth placeholder="Ej: rollo de cocina, paletas..."
                          value={it.productName || ''}
                          onChange={(e) => setItem(idx, { productName: e.target.value })}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small" type="number" value={it.quantity}
                        onChange={(e) => setItem(idx, { quantity: Math.max(0, Number(e.target.value)) })}
                        inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" fullWidth placeholder="Opcional"
                        value={it.notes || ''}
                        onChange={(e) => setItem(idx, { notes: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() =>
                        setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })
                      }><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {form.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">Agregá filas con lo que hace falta comprar.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear pedido'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
